import React from 'react';
import {Store} from '../../models/store.model';
import Modal from '../../../../Presentation/components/ui/Modal';
import {AppText} from '../../../../Presentation/components/ui/Typography';
import {AlertTriangle} from 'lucide-react';
import {useStoreDetails} from '../../hooks/useStoreDetails';
import {StoreDetailsHeader} from '../headers/StoreDetailsHeader';
import {StoreBasicInfo} from '../info-sections/StoreBasicInfo';
import {StoreStatusInfo} from '../info-sections/StoreStatusInfo';
import {StoreFinancialInfo} from '../info-sections/StoreFinancialInfo';
import {StoreLastVisitInfo} from '../info-sections/StoreLastVisitInfo';
import {StoreDocumentsInfo} from '../info-sections/StoreDocumentsInfo';
import {StoreImagesInfo} from '../info-sections/StoreImagesInfo';
import {StoreAdditionalInfo} from '../info-sections/StoreAdditionalInfo';
import {StoreDetailsBySections} from '../info-sections/StoreDetailsBySections';
import {StoreDeliveriesLinkCard} from '../deliveries/StoreDeliveriesLinkCard';

interface StoreDetailsModalProps {
  storeId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (store: Store) => void;
  onDelete?: (storeId: string) => void;
  stores?: Store[];
}

const StoreDetailsModal: React.FC<StoreDetailsModalProps> = ({
  storeId,
  isOpen,
  onClose,
  onEdit,
  onDelete,
  stores
}) => {
  const {
    store,
    loading,
    error
  } = useStoreDetails({storeId, isOpen, stores});

  const handleEdit = () => {
    if (store && onEdit) {
      onEdit(store);
      onClose();
    }
  };

  const handleDelete = () => {
    if (store && onDelete) {
      onDelete(store.id.toString());
      onClose();
    }
  };

  return (
    <>
      <Modal
        open={isOpen}
        onClose={onClose}
        title="تفاصيل المتجر"
        width="max-w-4xl"
      >
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
            <AppText className="mr-3 text-gray-600">جاري تحميل تفاصيل المتجر...</AppText>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <AppText className="text-red-600">{error}</AppText>
          </div>
        ) : store ? (
          <div className="space-y-6">
            <StoreDetailsHeader
              store={store}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />

            {store.sections && store.sections.length > 0 ? (
              <StoreDetailsBySections
                store={store}
                childrenAfterSections={
                  <StoreDeliveriesLinkCard
                    storeId={store.id.toString()}
                    storeName={store.name}
                  />
                }
              />
            ) : (
              <>
                <StoreBasicInfo store={store} />
                <StoreStatusInfo store={store} />
                <StoreFinancialInfo store={store} />
                <StoreDeliveriesLinkCard
                  storeId={store.id.toString()}
                  storeName={store.name}
                />
                <StoreLastVisitInfo store={store} />
                <StoreDocumentsInfo store={store} />
                <StoreImagesInfo store={store} />
                <StoreAdditionalInfo store={store} />
              </>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <AppText className="text-gray-600">لم يتم العثور على المتجر</AppText>
          </div>
        )}
      </Modal>
    </>
  );
};

export default StoreDetailsModal;
